// cosmos.ts — the Scene wrapper around the cosmos shader. Holds the interaction
// targets (pointer, scroll, warp) and eases them toward their goals so the
// world feels weighty instead of twitchy.

import { FullscreenMaterial, type Frame, type Globals, type Renderer, type Scene } from "../gfx";
import { COSMOS_WGSL } from "./cosmos.wgsl";

const ease = (current: number, target: number, rate: number, dt: number): number => {
  // frame-rate independent exponential smoothing
  const k = 1 - Math.exp(-rate * dt);
  return current + (target - current) * k;
};

export class Cosmos implements Scene {
  /** Target inputs, written by the app shell. */
  pointerTarget: [number, number] = [0, 0];
  scrollTarget = 0;
  warpTarget = 0;

  private pointer: [number, number] = [0, 0];
  private scroll = 0;
  private warp = 0;

  private readonly material: FullscreenMaterial;

  constructor(renderer: Renderer) {
    this.material = new FullscreenMaterial(renderer.gpu, renderer.globals, {
      label: "cosmos",
      source: COSMOS_WGSL,
    });
  }

  update(frame: Frame, globals: Globals): void {
    const dt = frame.delta;
    this.pointer[0] = ease(this.pointer[0], this.pointerTarget[0], 3.5, dt);
    this.pointer[1] = ease(this.pointer[1], this.pointerTarget[1], 3.5, dt);
    this.scroll = ease(this.scroll, this.scrollTarget, 5, dt);
    this.warp = ease(this.warp, this.warpTarget, 4, dt);

    globals.pointer = this.pointer;
    globals.scroll = this.scroll;
    globals.warp = this.warp;
    globals.time = frame.time;
  }

  draw(pass: GPURenderPassEncoder): void {
    this.material.draw(pass);
  }
}
